import React, { useEffect, useState } from "react";
import {
  deactivationSummary,
  openQuotationsText,
  replacementCandidates,
} from "../../lib/memberManagement";

/**
 * Деактивиране на потребител.
 *
 * WHAT THE DIALOG NEVER DOES
 *   * it never decides that a hand-over is needed: `impact.requires_replacement`
 *     and the open count come from the database, which refuses the deactivation
 *     anyway when a replacement is missing;
 *   * it never hides the reassignment. The number of affected offers is spelled
 *     out and the confirmation button says that the offers move;
 *   * it never offers an inactive person, or the person being deactivated, as the
 *     new owner of the offers.
 *
 * Requiring the selection before enabling the button is a courtesy; the database
 * validates the replacement inside the same transaction that deactivates.
 */
export const MemberDeactivateDialog = ({
  open,
  member,
  impact,
  loading,
  error,
  members,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [replacement, setReplacement] = useState("");

  useEffect(() => {
    if (open) setReplacement("");
  }, [open, member?.member_id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open || !member) return null;

  const needsReplacement = impact?.requires_replacement === true;
  const candidates = needsReplacement
    ? replacementCandidates(members, member.member_id)
    : [];
  const ready = !loading && !!impact && (!needsReplacement || !!replacement);
  const label = needsReplacement ? "Прехвърли офертите и деактивирай" : "Деактивирай";

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deactivate-member-title"
        data-testid="deactivate-member-dialog"
      >
        <div className="h" id="deactivate-member-title">
          Деактивиране на потребител
        </div>
        <div className="body">
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
            {member.email}
            {member.role_label ? ` — ${member.role_label}` : ""}
          </p>

          {loading && (
            <p className="qw-help" data-testid="deactivate-member-loading">
              Изчисляване на последствията…
            </p>
          )}

          {!loading && impact && (
            <>
              <p
                style={{ marginTop: 10, fontSize: 13, color: "#475569" }}
                data-testid="deactivate-member-summary"
              >
                {deactivationSummary(impact)}
              </p>

              {impact.terminal_quotations > 0 && (
                <p className="qw-help" data-testid="deactivate-member-history">
                  Приключените оферти остават записани на този потребител.
                </p>
              )}

              {needsReplacement && (
                <div className="qw-field" style={{ marginTop: 12 }}>
                  <label htmlFor="deactivate-replacement">Нов отговорник</label>
                  <select
                    id="deactivate-replacement"
                    className="qw-select"
                    value={replacement}
                    onChange={(e) => setReplacement(e.target.value)}
                    disabled={submitting || candidates.length === 0}
                    data-testid="deactivate-replacement-select"
                  >
                    <option value="">Изберете потребител</option>
                    {candidates.map((c) => (
                      <option key={c.member_id} value={c.member_id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <div className="qw-help" data-testid="deactivate-replacement-help">
                    {openQuotationsText(impact.open_quotations)} ще бъдат прехвърлени на
                    избрания потребител.
                  </div>
                  {candidates.length === 0 && (
                    <div className="qw-error" data-testid="deactivate-no-candidates">
                      Няма друг активен потребител, на който да прехвърлите офертите.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {error && (
            <div
              className="qw-error"
              role="alert"
              style={{ marginTop: 10 }}
              data-testid="deactivate-member-error"
            >
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="deactivate-member-cancel"
          >
            Отказ
          </button>
          <button
            type="button"
            className="qw-btn danger"
            onClick={() => onSubmit(replacement || null)}
            disabled={submitting || !ready}
            data-testid="deactivate-member-confirm"
          >
            {submitting ? "Деактивиране…" : label}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberDeactivateDialog;
